/**
 * Deletion log for project sync.
 *
 * Under the per-project merge (see `projectMerge.ts`), a project that is
 * simply ABSENT from the other side is kept, because absence usually
 * means "that device never saw it" rather than "that device deleted it".
 * That is the right default - it is what stops a stale device from
 * silently deleting projects it never knew about - but it means a real
 * deletion needs to be recorded explicitly, or deleting a project on one
 * device would just have it pushed back by the next sync from another,
 * forever.
 *
 * So `deleteProject()` writes a tombstone here, and tombstones ride
 * along inside the synced document. They are garbage-collected by
 * `mergeProjects()` once they age past `TOMBSTONE_TTL_MS` or once the
 * project is resurrected by a newer edit.
 *
 * Kept in its own localStorage key rather than inside `ProjectsState` on
 * purpose: the projects state is already at v5 with a four-step
 * migration chain, and a bookkeeping side-table does not justify a v6
 * bump and another migration to get wrong.
 */

import { createSignal } from 'solid-js';
import type { Tombstone } from './projectMerge.ts';

const KEY = 'opbuttons.projectTombstones.v1';

function load(): Tombstone[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is Tombstone =>
        typeof t === 'object' && t !== null
        && typeof (t as Tombstone).id === 'string'
        && typeof (t as Tombstone).deletedAt === 'number',
    );
  } catch {
    return [];
  }
}

const [signal, setSignal] = createSignal<Tombstone[]>(load());

/** Current tombstones. Reactive - reading this inside an effect tracks it. */
export const tombstones = signal;

function persist(next: Tombstone[]): void {
  setSignal(next);
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* quota - non-fatal */ }
}

/** Record that a project was deleted on this device. */
export function recordDeletion(id: string, at: number = Date.now()): void {
  const next = signal().filter((t) => t.id !== id);
  next.push({ id, deletedAt: at });
  persist(next);
}

/** Replace the whole log - used after a merge, so aged-out and
 *  resurrected entries stop travelling with every push. */
export function setTombstones(next: Tombstone[]): void {
  persist(next);
}

// ─── remote-presence bookkeeping (per-project doc split, 2026-08-31) ────
//
// Once each project is its OWN document, a project vanishing from the
// bridge's doc list is ambiguous in exactly the way absence always is:
// either another device deleted it, or this device created it and has
// not pushed yet. Tombstones answer that for OUR deletes; this answers
// it for THEIRS.
//
// We record which project ids we have actually SEEN on the bridge. An id
// in that set which later disappears from the doc list was deleted
// elsewhere. An id that was never in it is simply ours and unpushed.

const AGREED_KEY = 'opbuttons.projectSync.agreed.v1';

/**
 * The `modifiedAt` this device last AGREED with the bridge on, per
 * project id. Presence of a key means "we have seen this project on the
 * bridge"; the value means "and this is the version we agreed on".
 *
 * Both halves are needed, and a timestamp comparison cannot replace
 * either - which cost a debugging round to establish. The obvious
 * cheaper design is a plain seen-id set plus "was it modified since the
 * last sync?", but a project ADOPTED from another device carries THAT
 * device's `modifiedAt`, which is naturally newer than our last sync and
 * so is indistinguishable from a local edit by time alone. Storing the
 * agreed value makes the test exact: local differs from agreed IFF this
 * device changed it since.
 */
export function agreedModifiedAt(): Record<string, number> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(AGREED_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'number') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function setAgreedModifiedAt(next: Record<string, number>): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(AGREED_KEY, JSON.stringify(next)); } catch { /* quota */ }
}
